import React from 'react';

import { EMPTY_VALUE } from '@betterment/core';
import { Box, Text, ToggleTip } from '@betterment/samba-react';
import { formatNumberWithDecimals } from '@betterment/shared-components';
import { isNil } from 'es-toolkit/compat';

const formatPercentage = (value: number) => {
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumberWithDecimals(value, 2)}%`;
};

const conditionalStyling = (formattedReturn: string) => {
  if (formattedReturn.startsWith('+')) {
    return 'text.success';
  }
  return 'text.default';
};

type TimeWeightedReturnDisplayProps = {
  color: string;
  label: string;
  returnValue: number | null | undefined;
  displayDate: string | undefined;
  description?: string;
};

export const TimeWeightedReturnDisplay: React.FC<TimeWeightedReturnDisplayProps> = ({
  color,
  label,
  returnValue,
  displayDate,
  description,
}) => (
  <Box css={{ flex: 1 }}>
    <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.100', marginBottom: 'space.050' }}>
      <Box
        css={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      <Text variant="text50">{label}</Text>
      {description && (
        <ToggleTip>
          <ToggleTip.Trigger label="Portfolio Strategy Information" css={{ ml: 'space.50' }} />
          <ToggleTip.Content align="start" side="right" css={{ maxWidth: '300px', width: 'max-content' }}>
            Your current portfolio strategy is <br />
            {description}
          </ToggleTip.Content>
        </ToggleTip>
      )}
    </Box>
    <Box css={{ display: 'flex', flexDirection: 'column' }}>
      {!isNil(returnValue) && (
        <Text variant="text300" css={{ color: conditionalStyling(formatPercentage(returnValue)) }}>
          {formatPercentage(returnValue)}
        </Text>
      )}
      {isNil(returnValue) && (
        <Text variant="text300" css={{ color: 'text.default' }}>
          {EMPTY_VALUE}
        </Text>
      )}
      <Text variant="text50" css={{ color: 'text.secondary' }}>
        {displayDate || '\u00A0'}
      </Text>
    </Box>
  </Box>
);
